/**
 * Column definitions for expanded row table
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatNumberUsage } from '@/utils/function'
import { TFunction } from 'i18next'
import type { FieldValueResult } from './types'
import { getTemplateFields, getAllFields } from './constants'
import { shouldDisplayValue } from './utils'

/**
 * Create source column (main service / addon / summary)
 */
const createSourceColumn = (t: TFunction): DataTableColumn<any> => ({
    key: 'source',
    title: t('source', { defaultValue: 'Source' }),
    width: 180,
    render: (_, row) => {
        if (row._isSummary) {
            return (
                <div className="font-medium text-primary">
                    {t('total-config', { defaultValue: 'Cấu hình tổng' })}
                </div>
            )
        }
        if (row._isAddon) {
            // Extract addon name: remove "ADDONS" prefix, brackets, and template suffix
            const rawName = row.addon_service_name || ''
            const cleanName = rawName
                .replace(/^ADDONS?\s*/i, '')
                .replace(/^\[|\]$/g, '')
                .replace(/\s*-\s*[A-Za-z]+\d*$/i, '')
                .trim()
            const addonId = row.addon_service_id || ''
            const display =
                [addonId, cleanName].filter(Boolean).join(' - ') ||
                t('addon', { defaultValue: 'Addon' })
            return (
                <span className="text-primary text-xs inline-flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-current" />
                    {display}
                </span>
            )
        }
        return (
            <span className="text-primary">
                {row.service_name ||
                    row.service_id ||
                    t('main-service', { defaultValue: 'Main Service' })}
            </span>
        )
    },
})

/**
 * Create field column with value rendering and tooltip
 */
const createFieldColumn = (
    t: TFunction,
    field: { key: string; label: string }
): DataTableColumn<any> => ({
    key: field.key,
    title: t(field.label.toLowerCase(), { defaultValue: field.label }),
    width: field.key === 'server' ? 200 : 100,
    render: (_, row) => {
        const fieldData = row[`_field_${field.key}`] as
            | FieldValueResult
            | undefined

        // For server/processor, show model_name instead of value
        if (field.key === 'server') {
            if (!fieldData?.modelName) return null
            const modelDisplay = fieldData.modelName
            if (row._isSummary) {
                return (
                    <div className="font-medium text-primary">
                        {modelDisplay}
                    </div>
                )
            }
            return modelDisplay
        }

        if (!fieldData || !shouldDisplayValue(fieldData.value)) return null

        // Unit: uppercase, skip if 'qty'
        const rawUnit = fieldData.unit?.toLowerCase()
        const unit =
            rawUnit === 'qty' ? '' : fieldData.unit?.toUpperCase() || ''
        const displayValue = `${formatNumberUsage(fieldData.value)}${unit ? ` ${unit}` : ''}`

        // Build formula tooltip for VBM/Colocation
        const formulaTooltip = fieldData.formulaParts
            ?.map((part) => {
                if (part.modelName) {
                    return `(${part.modelName} - ${part.capacityValue}) x ${part.commitmentValue} ${unit}`.trim()
                }
                return `${part.capacityValue} x ${part.commitmentValue} ${unit}`.trim()
            })
            .join(' + ')

        if (row._isSummary) {
            if (formulaTooltip) {
                return (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="font-medium text-primary cursor-help inline-flex items-center gap-1 group">
                                    {displayValue}
                                    <span className="text-green-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                        ?
                                    </span>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{formulaTooltip}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )
            }
            return (
                <span className="font-medium text-primary">{displayValue}</span>
            )
        }

        // With formula tooltip
        if (formulaTooltip) {
            return (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help inline-flex items-center gap-1 group">
                                {displayValue}
                                <span className="text-gray-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                    ?
                                </span>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{formulaTooltip}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )
        }

        if (fieldData.details && fieldData.details.length > 0) {
            const detail = fieldData.details[0]
            return (
                <div className="text-xs">
                    <div>{displayValue}</div>
                    {detail.brand && (
                        <div className="text-gray-500">
                            {detail.brand} {detail.model && `- ${detail.model}`}
                        </div>
                    )}
                </div>
            )
        }

        return displayValue
    },
})

/**
 * Create pricing model column - only shows BUNDLE with tooltip
 */
const createPricingModelColumn = (t: TFunction): DataTableColumn<any> => ({
    key: 'pricing_model',
    title: t('pricing_model', { defaultValue: 'Gói' }),
    width: 80,
    render: (_, row) => {
        if (row._isSummary) return null
        const model = row._pricingModel?.toUpperCase()
        // Only show BUNDLE (with tooltip), hide PACKAGE
        if (model !== 'BUNDLE') return null

        return (
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 cursor-help">
                            {model}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t('bundle-pricing-tooltip', {
                            defaultValue: 'Giá = Σ (đơn giá × số lượng)',
                        })}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    },
})

/**
 * Create price recurring column
 */
const createPriceColumn = (t: TFunction): DataTableColumn<any> => ({
    key: 'price_recurring',
    title: t('unit_price', { defaultValue: 'Đơn giá' }),
    width: 120,
    cellClassName: 'text-right',
    render: (_, row) => {
        const price = row._priceRecurring
        if (price === undefined || price === null) return null
        const formatted = new Intl.NumberFormat('vi-VN').format(price)
        if (row._isSummary) {
            return <span className="font-medium text-primary">{formatted}</span>
        }
        return formatted
    },
})

/**
 * Create expanded row columns for service configoptions
 */
export const createExpandedRowColumns = (
    t: TFunction,
    serviceTemplate?: string
): DataTableColumn<any>[] => {
    const fields = serviceTemplate
        ? getTemplateFields(serviceTemplate)
        : getAllFields()

    const columns: DataTableColumn<any>[] = [createSourceColumn(t)]

    // Add field columns based on template
    fields.forEach((field) => {
        columns.push(createFieldColumn(t, field))
    })

    // Add pricing columns
    columns.push(createPricingModelColumn(t), createPriceColumn(t))

    return columns
}

// Backward compatibility alias
export const createExpandedRowColumnsByTemplate = createExpandedRowColumns
export const createConfigOptionsColumns = createExpandedRowColumns
export const createConfigOptionsColumnsByTemplate = createExpandedRowColumns
